import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, apiJson } from './client';
import { keys as hierarchyKeys } from './hierarchy';

export interface AdRights {
  reorder: boolean;
  migrate: boolean;
  share: boolean;
  dismiss: boolean;
}

export interface AdLinkDto {
  injectedLinkId: string;
  campaignId: string;
  creativeId: string;
  order: number;
  title: string;
  description: string | null;
  iconUrl: string | null;
  clickUrl: string;
  rights: AdRights;
}

export interface AdsForSet {
  adLinks: AdLinkDto[];
  realLinkCount: number;
}

export type AdBannerSlot = 'HEADER' | 'FOOTER' | 'SIDEBAR' | 'IN_GRID';

export interface AdBannerDto {
  campaignId: string;
  creativeId: string;
  slot: AdBannerSlot;
  htmlSandboxed: string | null;
  iconUrl: string | null;
  clickUrl: string | null;
}

export function useAdsBanners(slot: AdBannerSlot, enabled: boolean) {
  return useQuery({
    queryKey: ['ads', 'banners', slot],
    queryFn: () => api.get(`ads/banners?slot=${slot}`).json<{ banners: AdBannerDto[] }>(),
    enabled,
    staleTime: 60_000,
  });
}

export const adKeys = {
  forSet: (slug: string) => ['ads', 'for-set', slug] as const,
};

export function useAdsForSet(setSlug: string | null, enabled: boolean) {
  return useQuery({
    queryKey: adKeys.forSet(setSlug ?? ''),
    queryFn: () => api.get(`ads/for-set/${setSlug}`).json<AdsForSet>(),
    enabled: !!setSlug && enabled,
    staleTime: 60_000,
  });
}

export function useDismissAd() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { creativeId: string; setId?: string | null; setSlug?: string }) =>
      apiJson<{ success: true }>(`ads/dismiss/${p.creativeId}`, {
        method: 'POST',
        body: JSON.stringify({ setId: p.setId ?? null }),
      }),
    onSuccess: (_d, v) => {
      if (v.setSlug) {
        qc.invalidateQueries({ queryKey: adKeys.forSet(v.setSlug) });
        qc.invalidateQueries({ queryKey: hierarchyKeys.linksInSet(v.setSlug) });
      }
    },
  });
}

export function useReportBlocker() {
  return useMutation({
    mutationFn: () =>
      apiJson<{ success: true }>('ads/blocker-detected', { method: 'POST', body: '{}' }),
  });
}
